import { useState, useRef } from "react";
import type { ColumnDef, RowData } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLingui } from "@lingui/react";
import { Columns2, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface ColumnSelectorProps<TData extends RowData> {
  columns: ColumnDef<TData, unknown>[];
  visibility: Record<string, boolean>;
  onVisibilityChange: (id: string, visible: boolean) => void;
  /** Full column order (all column IDs). Selectable columns are shown in this order. */
  order?: string[];
  onOrderChange?: (newOrder: string[]) => void;
}

function getColId<TData>(col: ColumnDef<TData, unknown>): string {
  return (col as { accessorKey?: string }).accessorKey ?? col.id ?? "";
}

export function ColumnSelector<TData extends RowData>({
  columns,
  visibility,
  onVisibilityChange,
  order,
  onOrderChange,
}: ColumnSelectorProps<TData>) {
  const { i18n } = useLingui();
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const selectableColumns = columns.filter(
    (col) => col.enableHiding !== false && col.meta?.label,
  );

  const selectableIds = new Set(selectableColumns.map(getColId));

  // Order the selectable columns by their position in the full `order` array.
  const orderedColumns: ColumnDef<TData, unknown>[] = order
    ? order
        .filter((id) => selectableIds.has(id))
        .map((id) => selectableColumns.find((col) => getColId(col) === id))
        .filter((col): col is ColumnDef<TData, unknown> => col !== undefined)
    : selectableColumns;

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (dropIndex: number) => {
    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);

    if (
      fromIndex === null ||
      fromIndex === dropIndex ||
      !onOrderChange ||
      !order
    )
      return;

    // Reorder the selectable columns
    const newSelectableOrder = orderedColumns.map(getColId);
    const [moved] = newSelectableOrder.splice(fromIndex, 1);
    newSelectableOrder.splice(dropIndex, 0, moved!);

    // Rebuild the full order: non-selectable columns stay in place,
    // selectable columns take their new positions.
    let selectableIdx = 0;
    const newFullOrder = order.map((id) =>
      selectableIds.has(id) ? (newSelectableOrder[selectableIdx++] ?? id) : id,
    );

    onOrderChange(newFullOrder);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-11 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
        >
          <Columns2 className="size-4" />
          {i18n._("Columns")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-70 p-1.25">
        <div className="flex items-center px-3 py-3">
          <span className="flex-1 text-xl font-medium text-(--color-text-primary)">
            {i18n._("Columns")}
          </span>
        </div>
        <div className="h-px bg-(--color-surface-muted)" />
        <div className="flex max-h-105 flex-col gap-2 overflow-y-auto p-3">
          {orderedColumns.map((col, index) => {
            const id = getColId(col);
            const visible = visibility[id] ?? true;

            return (
              <div
                key={id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                onClick={() => onVisibilityChange(id, !visible)}
                className={cn(
                  "flex cursor-pointer select-none items-center gap-2 rounded-[8px] border bg-(--color-surface-muted) py-2 pl-2 pr-3 transition-colors",
                  dragOverIndex === index
                    ? "border-(--brand-black)"
                    : "border-(--color-border)",
                )}
              >
                <div
                  className="flex size-8 shrink-0 cursor-grab items-center justify-center rounded-[7px] border border-(--color-border) bg-(--color-surface-content) active:cursor-grabbing"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="size-4 text-(--color-text-secondary)" />
                </div>
                <span className="flex-1 text-sm font-medium text-(--color-text-primary)">
                  {col.meta!.label}
                </span>
                <Checkbox
                  checked={visible}
                  onCheckedChange={(checked) =>
                    onVisibilityChange(id, checked === true)
                  }
                  onClick={(e) => e.stopPropagation()}
                  className="size-5 rounded-[4px]"
                />
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
